/**
 * CSS Modules 类名工具函数
 *
 * 参考 reSKRipt 将样式导入转为函数调用的理念，提供类似的开发体验：
 *   import styles from './index.module.less';
 *   const cx = createCx(styles);
 *   <div className={cx('root', { active: isActive }, otherClass)} />
 *
 * 功能：
 * 1. 自动查找 CSS Modules 对象中的类名映射
 * 2. 支持条件类名（对象语法）
 * 3. 支持混合使用字符串和对象
 */

type CxInput = string | Record<string, boolean | undefined | null> | undefined | null | false;

/**
 * 创建绑定到特定 CSS Modules 对象的 cx 函数
 *
 * @example
 * ```tsx
 * import styles from './index.module.less';
 * import { createCx } from '@utils/cx';
 *
 * const cx = createCx(styles);
 *
 * // 简单用法
 * <div className={cx('root')} />
 *
 * // 条件类名
 * <div className={cx('menuItem', { menuItemActive: isActive })} />
 *
 * // 混合用法
 * <div className={cx('card', { cardHover: hovering }, 'extra')} />
 * ```
 */
export function createCx(styles: Record<string, string>) {
    return (...args: CxInput[]): string => {
        const classes: string[] = [];

        for (const arg of args) {
            if (!arg) {
                continue;
            }

            if (typeof arg === 'string') {
                // 如果是 CSS Modules 中的 key，映射到实际类名
                const mapped = styles[arg];
                classes.push(mapped || arg);
                continue;
            }

            // 对象语法：{ className: condition }
            for (const [key, value] of Object.entries(arg)) {
                if (value) {
                    const mapped = styles[key];
                    classes.push(mapped || key);
                }
            }
        }

        return classes.join(' ');
    };
}

/**
 * 通用的 classnames 工具函数（不绑定 CSS Modules）
 *
 * @example
 * ```tsx
 * <div className={cx('btn', { 'btn-primary': isPrimary })} />
 * ```
 */
export function cx(...args: CxInput[]): string {
    const classes: string[] = [];

    for (const arg of args) {
        if (!arg) {
            continue;
        }

        if (typeof arg === 'string') {
            classes.push(arg);
            continue;
        }

        for (const [key, value] of Object.entries(arg)) {
            if (value) {
                classes.push(key);
            }
        }
    }

    return classes.join(' ');
}

interface SemanticClassNames {
    [key: string]: string | SemanticClassNames | null | undefined;
}

/**
 * 递归深度合并两个 Semantic classNames 对象
 *
 * 合并策略：
 * - 字符串 + 字符串：拼接（如 `"a" + "b"` : `"a b"`）
 * - 对象 + 对象：递归合并（支持嵌套结构如 `popup: { root: '...' }`）
 * - 仅 base 有值（custom 为 null/undefined）：取 base 值
 * - 类型不一致或其余情况：以 base 为准
 */
function deepMergeClassNames<T extends SemanticClassNames>(base: T, custom: SemanticClassNames): T {
    // 以 custom 为底，逐个用 base 的 key 覆盖/合并，保留 custom 中多出的 key
    const merged: SemanticClassNames = {...custom};
    for (const [key, baseValue] of Object.entries(base)) {
        const customValue = custom[key];
        if (typeof baseValue === 'string' && typeof customValue === 'string') {
            // 双方都是类名字符串  空格拼接
            merged[key] = `${baseValue} ${customValue}`;
        } else if (
            typeof baseValue === 'object' &&
            baseValue &&
            typeof customValue === 'object' &&
            customValue
        ) {
            // 双方都是嵌套对象（如 popup: { root: '...' }） 递归合并
            merged[key] = deepMergeClassNames(baseValue, customValue);
        } else {
            // 类型不一致 / custom 侧为 null·undefined  以 base 为准
            merged[key] = baseValue;
        }
    }
    return merged as T;
}

/**
 * 合并组件内置 Semantic classNames 与用户自定义 classNames
 *
 * 配合 antd Semantic DOM 的 classNames 属性使用，支持对象 / 函数两种形态，
 * 对 baseClassNames 中的每个 key 自动递归合并内置样式与用户样式。
 */
export function mergeSemanticClassNames<T extends SemanticClassNames>(
    baseClassNames: T,
    customClassNames?: SemanticClassNames | ((info: any) => SemanticClassNames) | null,
): T {
    if (!customClassNames) {
        return baseClassNames;
    }

    if (typeof customClassNames === 'function') {
        return ((info: any) =>
            deepMergeClassNames(baseClassNames, customClassNames(info))) as unknown as T;
    }

    return deepMergeClassNames(baseClassNames, customClassNames);
}
